import React, { useEffect, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  startOfDay,
  formatISO,
  endOfDay,
} from 'date-fns';
import Radio from './Radio';

const DateRangePicker = ({
  onChange,
  dateRange: initialDaterange,
  startDate: initialStartDate,
  endDate: initialEndDate,
}) => {
  const [dateRange, setDateRange] = useState(initialDaterange || 'lastMonth');
  const [startDate, setStartDate] = useState(initialStartDate || '');
  const [endDate, setEndDate] = useState(initialEndDate || '');

  useEffect(() => {
    updateDateRange(dateRange);
  }, [dateRange]);

  const updateDateRange = range => {
    let newStartDate, newEndDate;

    switch (range) {
      case 'today':
        newStartDate = startOfDay(new Date());
        newEndDate = endOfDay(new Date());
        break;
      case 'currentMonth':
        newStartDate = startOfMonth(new Date());
        newEndDate = endOfMonth(new Date());
        break;
      case 'last7Days':
        newStartDate = subDays(startOfDay(new Date()), 6);
        newEndDate = startOfDay(new Date());
        break;
      case 'lastMonth':
        newStartDate = startOfMonth(subMonths(new Date(), 1));
        newEndDate = endOfMonth(subMonths(new Date(), 1));
        break;
      case 'all':
        newStartDate = '';
        newEndDate = '';
        break;
      case 'custom':
        return; // Return early without updating the state for custom dates
      default:
        newStartDate = '';
        newEndDate = '';
    }

    if (newStartDate) {
      setStartDate(formatISO(newStartDate, { representation: 'complete' }));
    } else {
      setStartDate('');
    }

    if (newEndDate) {
      setEndDate(formatISO(newEndDate, { representation: 'complete' }));
    } else {
      setEndDate('');
    }
  };

  const handleDateRangeChange = e => {
    setDateRange(e.target.value);
  };

  useEffect(() => {
    onChange({ startDate, endDate, dateRange });
  }, [startDate, endDate]);

  return (
    <div className="space-y-2">
      <Radio.Group name="dateRange" id="dateRange">
        <Radio.Wrapper>
          <Radio.Input
            value="lastMonth"
            id="lastMonth"
            name="dateRange"
            checked={dateRange === 'lastMonth'}
            onChange={handleDateRangeChange}
          />

          <Radio.Label htmlFor="lastMonth">Last month</Radio.Label>
        </Radio.Wrapper>

        <Radio.Wrapper>
          <Radio.Input
            value="currentMonth"
            id="currentMonth"
            name="dateRange"
            checked={dateRange === 'currentMonth'}
            onChange={handleDateRangeChange}
          />

          <Radio.Label htmlFor="currentMonth">Current month</Radio.Label>
        </Radio.Wrapper>

        <Radio.Wrapper>
          <Radio.Input
            value="today"
            id="today"
            name="dateRange"
            checked={dateRange === 'today'}
            onChange={handleDateRangeChange}
          />

          <Radio.Label htmlFor="today">Today</Radio.Label>
        </Radio.Wrapper>

        <Radio.Wrapper>
          <Radio.Input
            value="last7Days"
            id="last7Days"
            name="dateRange"
            checked={dateRange === 'last7Days'}
            onChange={handleDateRangeChange}
          />

          <Radio.Label htmlFor="last7Days">Last 7 days</Radio.Label>
        </Radio.Wrapper>

        <Radio.Wrapper>
          <Radio.Input
            value="all"
            id="all"
            name="dateRange"
            checked={dateRange === 'all'}
            onChange={handleDateRangeChange}
          />

          <Radio.Label htmlFor="all">All</Radio.Label>
        </Radio.Wrapper>

        <Radio.Wrapper>
          <Radio.Input
            value="custom"
            id="custom"
            name="dateRange"
            checked={dateRange === 'custom'}
            onChange={handleDateRangeChange}
          />

          <Radio.Label htmlFor="custom">Custom</Radio.Label>
        </Radio.Wrapper>

        {dateRange === 'custom' && (
          <div className="flex gap-x-2 mt-4 items-center ml-6">
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="mt-1 py-1 px-3 rounded border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span>-</span>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="mt-1 py-1 px-3 rounded border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        )}
      </Radio.Group>
    </div>
  );
};

export default DateRangePicker;
